import { useRef, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { Button, Paginator, cn } from '@jeraisonne/ui';
import { usePagedList } from '@/lib/use-paged-list';
import { DOCUMENT_ACCEPT, DocumentError, type DocumentRow } from '@jeraisonne/documents';
import { useAuth } from '@/lib/auth';
import { ContactSearchInput } from '@/components/contacts/contact-search-input';
import { DocumentItem } from '@/components/documents/document-item';
import { useDeleteDocument, useUploadDocument, useVisibleDocuments } from '@/lib/documents/queries';
import type { DocumentDestination } from '@/lib/documents/destinations';
import {
  DOCUMENT_SCOPE_FILTERS,
  DOCUMENT_SCOPE_LABELS,
  canDeleteDocument,
  documentMeta,
  filterDocuments,
  groupDocuments,
  type DocumentScopeFilter,
} from '@/lib/documents/list-helpers';

/**
 * Page « Mes fichiers » : TOUT ce que l'utilisateur peut voir (fichiers de ses
 * contacts, de ses classes, de ses réunions — la RLS de `documents` fait le tri),
 * regroupé par contexte, avec recherche, filtre par portée et dépôt.
 *
 * Vue partagée tuteur / élève : seules changent les DESTINATIONS proposées au
 * dépôt (le tuteur peut aussi déposer dans une classe ; l'élève jamais — miroir
 * de la RLS). Mobile-first : une colonne, cibles ≥ 44 px, boutons pleine largeur
 * sous `sm`, bas de page protégé par `pb-safe`.
 */
export function FilesPageView({
  destinations,
  destinationsLoading,
}: {
  destinations: DocumentDestination[];
  destinationsLoading: boolean;
}) {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const visible = useVisibleDocuments();
  const upload = useUploadDocument();
  const remove = useDeleteDocument();

  const inputRef = useRef<HTMLInputElement>(null);
  const [destinationKey, setDestinationKey] = useState('');
  const [scope, setScope] = useState<DocumentScopeFilter>('all');
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // L'ajout est replié dès qu'il y a des fichiers : la liste passe en premier.
  const [adding, setAdding] = useState(false);

  const destination = destinations.find((d) => d.key === destinationKey) ?? null;
  const docs = visible.data ?? [];
  const filtered = filterDocuments(docs, { scope, query }, userId);
  const allGroups = groupDocuments(filtered, userId);
  // Pagination (20 fichiers par page) dans l'ordre des conversations ; l'en-tête
  // de chaque groupe garde son effectif total.
  const groupTotals = new Map(allGroups.map((g) => [g.key, g.documents.length]));
  const { items: pageDocs, paginator } = usePagedList(
    allGroups.flatMap((g) => g.documents),
    { resetKeys: [scope, query], unit: 'fichiers', label: 'Pages des fichiers' },
  );
  const groups = groupDocuments(pageDocs, userId);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0 || !destination) return;
    const picked = Array.from(files);
    setMessage(null);
    for (const file of picked) {
      setProgress(picked.length > 1 ? `Envoi de « ${file.name} »…` : 'Envoi en cours…');
      try {
        await upload.mutateAsync({ file, target: destination.target });
      } catch (err) {
        setMessage(
          err instanceof DocumentError
            ? err.message
            : "Le fichier n'a pas pu être envoyé. Réessaie.",
        );
        break;
      }
    }
    setProgress(null);
  }

  async function handleDelete(doc: DocumentRow) {
    if (
      !window.confirm(
        `Supprimer « ${doc.fileName} » ? Le fichier sera supprimé pour tout le monde.`,
      )
    )
      return;
    setMessage(null);
    setDeletingId(doc.id);
    try {
      await remove.mutateAsync({ id: doc.id, storagePath: doc.storagePath });
    } catch (err) {
      setMessage(err instanceof DocumentError ? err.message : 'Suppression impossible.');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="pb-safe mx-auto max-w-3xl px-4 py-6 sm:py-8">
      <header className="mb-5">
        <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
          Mes fichiers
        </h1>
        <p className="text-ink-500 mt-1 font-sans text-sm">
          Les fichiers que tu partages et reçois : avec tes contacts, tes classes et pendant tes
          rendez-vous.
        </p>
      </header>

      {/* Dépôt : choisir OÙ, puis le fichier (replié quand il y a déjà des fichiers) */}
      {!adding && !visible.isLoading && docs.length > 0 ? (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="border-ink-200 text-ink-700 hover:border-primary-400 ocean:bg-white/5 mb-5 flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 rounded-2xl border border-dashed bg-white font-sans text-sm font-semibold active:scale-[0.98]"
        >
          + Ajouter un fichier
        </button>
      ) : (
        <section
          aria-labelledby="files-add-title"
          className="border-ink-100 mb-5 space-y-3 rounded-2xl border bg-white p-4"
        >
          <h2 id="files-add-title" className="text-ink-900 text-sm font-bold">
            Ajouter un fichier
          </h2>
          {destinations.length === 0 && !destinationsLoading ? (
            <p className="text-ink-500 text-sm">
              Tu pourras déposer un fichier dès que tu auras un contact lié.
            </p>
          ) : (
            <>
              <div>
                <label
                  htmlFor="files-destination"
                  className="text-ink-700 mb-1.5 block text-xs font-semibold"
                >
                  Où l’ajouter ?
                </label>
                <select
                  id="files-destination"
                  value={destinationKey}
                  onChange={(e) => setDestinationKey(e.target.value)}
                  disabled={destinationsLoading}
                  className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-400 h-11 w-full appearance-none rounded-xl border px-3 font-sans text-sm focus:outline-none"
                >
                  <option value="">Choisir un contact ou une classe…</option>
                  {(['contact', 'class'] as const).map((group) => {
                    const items = destinations.filter((d) => d.group === group);
                    if (items.length === 0) return null;
                    return (
                      <optgroup key={group} label={group === 'contact' ? 'Contacts' : 'Classes'}>
                        {items.map((d) => (
                          <option key={d.key} value={d.key}>
                            {d.label}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
              </div>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={DOCUMENT_ACCEPT}
                className="hidden"
                aria-label="Choisir des fichiers à ajouter"
                onChange={(e) => {
                  const files = e.target.files;
                  void handleFiles(files).finally(() => {
                    e.target.value = '';
                  });
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
                disabled={!destination}
                loading={progress !== null}
                onClick={() => inputRef.current?.click()}
              >
                <Paperclip className="mr-2 size-4" aria-hidden />
                Choisir un fichier
              </Button>
              <p className="text-ink-400 text-xs">
                Images, PDF, Word, Excel, PowerPoint — 20 Mo max.
              </p>
            </>
          )}
          {progress && (
            <p role="status" className="text-ink-500 text-xs">
              {progress}
            </p>
          )}
        </section>
      )}

      {message && (
        <p role="alert" className="text-accent-700 mb-4 text-sm font-medium">
          {message}
        </p>
      )}

      {/* Recherche + filtre par portée */}
      <div className="mb-4 space-y-3">
        <ContactSearchInput
          value={query}
          onChange={setQuery}
          placeholder="Rechercher un fichier…"
          ariaLabel="Rechercher dans mes fichiers"
        />
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer par type">
          {DOCUMENT_SCOPE_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              aria-pressed={scope === f.value}
              onClick={() => setScope(f.value)}
              className={cn(
                'min-h-[2.75rem] rounded-full border px-4 font-sans text-sm font-semibold transition-colors active:scale-[0.98]',
                scope === f.value
                  ? 'border-primary-500 bg-primary-500 text-white'
                  : 'border-ink-200 text-ink-700 bg-white',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {visible.isLoading && <p className="text-ink-400 text-sm">Chargement de tes fichiers…</p>}

      {visible.isError && (
        <div role="alert" className="text-accent-700 text-sm">
          Impossible de charger tes fichiers.{' '}
          <button
            type="button"
            onClick={() => void visible.refetch()}
            className="min-h-[2.75rem] font-semibold underline underline-offset-2"
          >
            Réessayer
          </button>
        </div>
      )}

      {!visible.isLoading && !visible.isError && docs.length === 0 && (
        <div className="border-ink-200 rounded-2xl border bg-white p-6 text-center">
          <p className="text-ink-500 font-sans text-sm">
            Aucun fichier pour l’instant. Ceux que tu partages avec tes contacts, tes classes et
            pendant tes rendez-vous apparaîtront ici.
          </p>
        </div>
      )}

      {docs.length > 0 && allGroups.length === 0 && (
        <p className="text-ink-500 py-6 text-center text-sm">
          Aucun fichier ne correspond à ta recherche.
        </p>
      )}

      <div className="space-y-6">
        {groups.map((group) => (
          <section key={group.key} aria-label={group.label}>
            <h2 className="font-display text-ink-500 mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[.1em]">
              <span className="bg-ink-100 text-ink-600 rounded-full px-2 py-0.5 text-[10px]">
                {DOCUMENT_SCOPE_LABELS[group.scope]}
              </span>
              <span className="min-w-0 truncate">{group.label}</span>
              <span className="text-ink-400 shrink-0 font-semibold normal-case tracking-normal">
                {groupTotals.get(group.key) ?? group.documents.length}
              </span>
            </h2>
            <ul className="space-y-2">
              {group.documents.map((doc) => (
                <DocumentItem
                  key={doc.id}
                  doc={doc}
                  meta={documentMeta(doc, userId)}
                  canDelete={canDeleteDocument(doc, userId)}
                  deleting={deletingId === doc.id}
                  onDelete={(d) => void handleDelete(d)}
                  onError={setMessage}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
      <Paginator {...paginator} />
    </div>
  );
}
